import { register } from 'swiper/element/bundle'
// Register Swiper custom elements
register()

export class SwiperInit {
  constructor() {
    // Initialize swipers
    this.initializeSwipers()

    // Re-init for Shopify editor
    if (Shopify.designMode) {
      document.addEventListener('shopify:section:load', () => this.initializeSwipers())
    }
  }

  initializeSwipers() {
    const swipers = document.querySelectorAll('swiper-container')

    swipers.forEach(swiperEl => {
      // Get basic settings from data attributes
      const mobileSlidesPerView = swiperEl.dataset.mobileSlidesPerView || 'auto'
      const desktopSlidesPerView = swiperEl.dataset.desktopSlidesPerView || 'auto'
      const tabletSlidesPerView = swiperEl.dataset.tabletSlidesPerView === '0' || !swiperEl.dataset.tabletSlidesPerView ? mobileSlidesPerView : swiperEl.dataset.tabletSlidesPerView
      const hasTextPagination = swiperEl.dataset.textPagination === 'true'

      // Get space between settings from data attributes
      const spaceBetweenMobile = Number(swiperEl.dataset.spaceBetweenMobile) || 16
      const spaceBetweenTablet = Number(swiperEl.dataset.spaceBetweenTablet) || 32
      const spaceBetweenDesktop = Number(swiperEl.dataset.spaceBetweenDesktop) || 32

      // Get layout settings
      const mobileLayout = swiperEl.dataset.mobileLayout || 'slider'
      const desktopLayout = swiperEl.dataset.desktopLayout || 'slider'

      // Setup media queries
      const mobile = window.matchMedia('(min-width: 0px) and (max-width: 1023px)')
      const desktop = window.matchMedia('(min-width: 1024px)')

      // Handle video playback in slides
      const handleVideoPlayback = () => {
        // Pause all videos first
        const videos = swiperEl.querySelectorAll('video')
        videos.forEach(video => {
          if (!video.classList.contains('section__wrapper-background')) {
            video.pause()
          }
        })

        // Play videos in visible slides
        const visibleSlides = Array.from(swiperEl.querySelectorAll('swiper-slide')).filter(
          slide => slide.classList.contains('swiper-slide-visible')
        )

        visibleSlides.forEach(slide => {
          const video = slide.querySelector('video')
          if (video && video.hasAttribute('autoplay')) {
            video.play()
          }
        })
      }

      // Handle text pagination updates
      const updateTextPagination = () => {
        const parentContainer = swiperEl.closest('.slideshow-text-controls__wrapper')
        if (!parentContainer) return

        const activeSlide = swiperEl.swiper.slides[swiperEl.swiper.realIndex]
        if (!activeSlide) return

        const activeSlideId = activeSlide.getAttribute('data-slide')
        const textItems = parentContainer.querySelectorAll('.slideshow-text-controls__item')

        textItems.forEach(item => {
          item.classList.toggle('active', item.getAttribute('data-slide') === activeSlideId)
        })
      }

      // Initialize thumbnails navigation
      const initThumbnails = () => {
        const thumbnailsContainer = swiperEl.closest('.main-product__media-wrapper')?.querySelector('.swiper-thumbnails .swiper')
        if (!thumbnailsContainer) return

        const thumbnailsParams = {
          direction: 'vertical',
          slidesPerView: 'auto',
          spaceBetween: 8,
          watchSlidesProgress: true,
          navigation: {
            nextEl: '.swiper-button-thumbs-next',
            prevEl: '.swiper-button-thumbs-prev',
          },
          breakpoints: {
            0: {
              direction: 'horizontal',
              slidesPerView: 4,
            },
            768: {
              direction: 'vertical',
              slidesPerView: 'auto',
            }
          }
        }

        Object.assign(thumbnailsContainer, thumbnailsParams)
        thumbnailsContainer.initialize()
      }

      // Setup text pagination click handlers
      const setupTextPaginationControls = () => {
        if (!hasTextPagination) return

        const parentContainer = swiperEl.closest('.slideshow-text-controls__wrapper')
        if (!parentContainer) return

        const textItems = parentContainer.querySelectorAll('.slideshow-text-controls__item')
        textItems.forEach(item => {
          item.addEventListener('click', () => {
            const slideId = item.getAttribute('data-slide')
            const slideIndex = Array.from(swiperEl.swiper.slides).findIndex(
              slide => slide.getAttribute('data-slide') === slideId
            )
            if (slideIndex !== -1) {
              swiperEl.swiper.slideTo(slideIndex)
            }
          })
        })
      }

      // Handle layout changes
      const buildSlider = () => {
        const shouldBeSlider = (desktop.matches && desktopLayout === 'slider') ||
                             (mobile.matches && mobileLayout === 'slider')

        if (shouldBeSlider) {
          if (!swiperEl.swiper || swiperEl.swiper.destroyed) {
            // Get initial values from data attributes
            const initialMobileSlidesPerView = parseFloat(swiperEl.dataset.mobileSlidesPerView) || 'auto'
            const initialTabletSlidesPerView = swiperEl.dataset.tabletSlidesPerView === '0' || !swiperEl.dataset.tabletSlidesPerView ? initialMobileSlidesPerView : parseFloat(swiperEl.dataset.tabletSlidesPerView)
            const initialDesktopSlidesPerView = parseFloat(swiperEl.dataset.desktopSlidesPerView) || 'auto'

            // Basic swiper parameters
            const params = {
              ...(swiperEl.parentElement?.classList.contains('card__image') ? {
                virtual: {
                  enabled: true,
                },
                nested: true
              } : {}),
              breakpoints: {
                0: {
                  slidesPerView: initialMobileSlidesPerView,
                  spaceBetween: parseInt(spaceBetweenMobile)
                },
                768: {
                  slidesPerView: initialTabletSlidesPerView,
                  spaceBetween: parseInt(spaceBetweenTablet)
                },
                1024: {
                  slidesPerView: initialDesktopSlidesPerView,
                  spaceBetween: parseInt(spaceBetweenDesktop),
                }
              }
            }

            // Set parameters
            Object.assign(swiperEl, params)

            // Add event listeners before initialization
            swiperEl.addEventListener('swiperafterinit', () => {
              const parentSection = swiperEl.closest('.shopify-section')

              if (parentSection) {
                parentSection.classList.add('slider-enabled')
              }

              // Wait for the next tick to ensure Swiper is fully initialized
              setTimeout(() => {
                if (!swiperEl.swiper) {
                  return
                }

                // Adjust slidesPerView after initialization
                const totalSlides = swiperEl.swiper?.slides?.length || 0;

                // Adjust slidesPerView if there are fewer slides than the setting
                const adjustedMobileSlidesPerView = totalSlides < initialMobileSlidesPerView ? totalSlides : initialMobileSlidesPerView;
                const adjustedTabletSlidesPerView = totalSlides < initialTabletSlidesPerView ? totalSlides : initialTabletSlidesPerView;
                const adjustedDesktopSlidesPerView = totalSlides < initialDesktopSlidesPerView ? totalSlides : initialDesktopSlidesPerView;

                // Update the slidesPerView settings
                if (swiperEl.swiper && swiperEl.swiper.params && swiperEl.swiper.params.breakpoints) {
                  swiperEl.swiper.currentBreakpoint = false;
                  swiperEl.swiper.params.breakpoints[0].slidesPerView = adjustedMobileSlidesPerView;
                  swiperEl.swiper.params.breakpoints[768].slidesPerView = adjustedTabletSlidesPerView;
                  swiperEl.swiper.params.breakpoints[992].slidesPerView = adjustedDesktopSlidesPerView;

                  // Force update the swiper
                  swiperEl.swiper.update();
                  swiperEl.swiper.slideTo(0, 0, false);
                }

                handleVideoPlayback()
                initThumbnails()
              }, 0)
            })

            // Listen for slide changes
            swiperEl.addEventListener('swiperslidechange', () => {
              handleVideoPlayback()
              updateTextPagination()
            })

            // Listen for swiper destruction
            swiperEl.addEventListener('swiperdestroy', () => {
              const parentSection = swiperEl.closest('.shopify-section')
              if (parentSection) {
                parentSection.classList.remove('slider-enabled')
              }
            })

            // Listen for lock
            swiperEl.addEventListener('swiperlock', () => {
              const parentSection = swiperEl.closest('.shopify-section')
              if (parentSection && !parentSection.classList.contains('recommended-products')) {
                setTimeout(() => {
                  const sliderNavs = parentSection.querySelectorAll('.grid-content__slider-nav')
                  sliderNavs.forEach(nav => {
                    nav.style.display = 'none'
                  })
                }, 1)
              }
            })

            // Initialize swiper after setting up event listeners
            swiperEl.initialize()
            if (hasTextPagination) {
              setupTextPaginationControls()
              updateTextPagination()
            }
          } else {
            swiperEl.swiper.update()
          }
        } else {
          // Initialize empty swiper
          Object.assign(swiperEl)
          swiperEl.initialize()
          if (swiperEl.swiper) {
            swiperEl.swiper.destroy(true, true)
            const parentSection = swiperEl.closest('.shopify-section')
            if (parentSection) {
              parentSection.classList.remove('slider-enabled')
            }
          }
        }
      }

      // Add resize listener
      window.addEventListener('resize', buildSlider)

      // Initial check
      buildSlider()
    })
  }
}

new SwiperInit()

